// -----------------------------------------------------------------------------
// 初期設定 - バックエンド応答毎の個別メッセージ
// -----------------------------------------------------------------------------
import action from './action';
import snackbar from './snackbar';
import helper, {
  HEADER_AUTHORIZED,
  HEADER_RUN_STATUS,
} from './helper';

/**
 * バックエンド応答毎の個別メッセージ取得
 *
 * @param {AxiosResponseHeaders} headers 応答ヘッダー
 * @param {number} statusCode HTTPスタータスコード
 * @return {Promise<object>} メッセージ情報 (個別メッセージなしの場合はヌル)
 */
export async function backendError(headers, statusCode) {
  // バックエンドの応答でない場合は処理終了
  const authHeader = helper.parseHeader(headers, HEADER_AUTHORIZED);
  if (!authHeader) {
    return null;
  }

  // エラー理由取得 - 認証情報ハッシュの付加情報 or 業務運用状態
  let reason = null;
  switch (statusCode) {
    case 403:
      reason = authHeader.option('E');
      break;
    case 503:
      reason = helper.parseHeader(headers, HEADER_RUN_STATUS)?.value;
      break;
    default: break;
  }

  // ステータスコード/エラー理由毎のメッセージ
  return backendMessage(statusCode, reason);
};

/**
 * バックエンド応答ステータス/エラー理由毎のメッセージ取得
 *
 * @param {number} statusCode HTTPスタータスコード
 * @param {string|null} reason エラー理由
 * @return {object} メッセージ情報 (個別メッセージなしの場合はヌル)
 */
export async function backendMessage(statusCode, reason) {
  const key = [statusCode, reason].join('/');
  const error = BACKEND_ERRORS[key] || BACKEND_ERRORS[statusCode];
  if (!error) {
    return null;
  }

  // メニュー有効/無効判定
  //   運用状態変更後に有効なメニュー項目があるかどうか判定
  //   メニューが有効(画面操作継続可能)な場合と無効な場合で
  //   メッセージとボタンアクション(遷移先画面)を分ける
  if (error.hasMenu) {
    const available = await action.menuAvailable();
    const result = available ? error['hasMenu'] : error['noMenu'];
    error.message = result.message;
    error.action = result.action;
  }
  return error;
};

//
// ステータスコード/エラー理由毎のメッセージ
//
const BACKEND_ERRORS = {
  // 403 認証トークン不正 - アクセス許可なしメッセージ
  '403/none': {
    reason: 'Invalid token',
    message: 'TKSCS00071E',
  },

  // 403 存在しないユーザー - ユーザー無効メッセージ
  '403/unknown': {
    reason: 'Unknown user',
    message: 'TKSCZ00025E',
  },

  // 403 無効状態のユーザー - ユーザー無効メッセージ
  '403/invalid': {
    reason: 'Invalid user',
    message: 'TKSCZ00025E',
  },

  // 403 期限切れのユーザー - ユーザー期限切れメッセージ
  '403/expired': {
    reason: 'Expired user',
    message: 'TKSCZ00022E',
  },

  // 403 実行環境不正 - アクセス許可なしメッセージ
  '403/environ': {
    reason: 'Bad environment',
    message: 'TKSCS00071E',
  },

  // 403 アクセス権なし - アクセス許可なしメッセージ
  '403/forbidden': {
    reason: 'No permission',
    message: 'TKSCS00071E',
  },

  // 403 アクセス拒否 - アクセス許可なしメッセージ
  '403': {
    reason: 'Forbidden',
    message: 'TKSCS00071E',
  },

  // 409 ユーザー情報変更検知
  '409': {
    reason: 'User modified',
    message: 'TKSCS00126I',          // ユーザー変更メッセージ
    action: action.reloadUser,       // ユーザー情報再読み込み
  },

  // 410 古いバージョンのAPI
  '410': {
    reason: 'Too old API',
    message: 'TKSCS00127I',          // アプリ更新メッセージ
    action: action.superReload,      // フロントエンド再読み込み
  },

  // 500 システム異常 (バックエンド業務APが意図的に500応答する訳ではない)
  '500': {
    reason: 'API failure',
    message: 'TKSCS00001S',          // システム異常メッセージ
    lowPriority: true,               // 他メッセージがない場合に表示
  },

  // 503 業務閉塞 (メンテナンス中)
  '503/block': {
    reason: 'Out of service',
    hasMenu: {                          // 有効なメニュー項目があれば
      message: 'TKSCS00133I',           // 業務閉塞/継続利用メッセージ
      action: action.reloadMenu,        // メニュー再読み込み
    },
    noMenu: {                           // 有効なメニュー項目がなければ
      message: 'TKSCS00134I',           // 業務閉塞/利用不可メッセージ
      action: action.backToLogin,       // ログインページに戻る
    },
    snackUpdate: snackbar.clearComing,  // 運用状態予定メッセージ消去
  },

  // 503 業務終了 (業務時間外)
  '503/close': {
    reason: 'Closed business',
    hasMenu: {                          // 有効なメニュー項目があれば
      message: 'TKSCS00131I',           // 業務終了/継続利用メッセージ
      action: action.reloadMenu,        // メニュー再読み込み
    },
    noMenu: {                           // 有効なメニュー項目がなければ
      message: 'TKSCS00132I',           // 業務終了/利用不可メッセージ
      action: action.backToLogin,       // ログインページに戻る
    },
    snackUpdate: snackbar.clearComing,  // 運用状態予定メッセージ消去
  },

  // 503 業務開始 (エラーではなく業務時間外から業務時間内への状態変化)
  '503/open': {
    reason: 'Opened business',
    message: 'TKSCS00130I',             // 業務開始メッセージ
    action: action.reloadMenu,          // メニュー再読み込み
    snackUpdate: snackbar.clearComing,  // 運用状態予定メッセージ消去
  },

  // 504 SQLタイムアウト (クエリータイムアウト/トランザクションタイムアウト)
  '504': {
    reason: 'SQL timeout',
    message: 'TKSCS00129E',             // 対象件数過多メッセージ
  },
};

// -----------------------------------------------------------------------------
// HTTPステータス毎の個別メッセージ
// -----------------------------------------------------------------------------
/**
 * HTTPスタータス毎の個別メッセージ取得
 * (バックエンドではなくAWSサービスが応答するHTTPステータス)
 *
 * @param {number} statusCode HTTPスタータスコード
 * @return {object} メッセージ情報 (個別メッセージなしの場合はヌル)
 */
export function serviceError(statusCode) {
  return SERVICE_ERRORS[statusCode];
};

//
// HTTPスタータス毎の個別メッセージ
//
const SERVICE_ERRORS = {
  // Forbidden リクエスト拒否 - アクセス許可なしメッセージ
  403: {message: 'TKSCS00071E'},

  // Not Found リクエストURL誤り - 該当データなしメッセージ
  404: {message: 'TKSCS00125E'},

  // Too Many Requests リクエスト過多 - アクセス集中メッセージ
  429: {message: 'TKSCS00128E'},

  // Gateway Timeout 応答タイムアウト - 対象件数過多メッセージ
  //                 (おそらく API Gateway の29秒タイムアウト)
  504: {message: 'TKSCS00129E'},
};

// -----------------------------------------------------------------------------
// エクスポート
// -----------------------------------------------------------------------------
export default {
  backendMessage,
  backendError,
  serviceError,
};
